import * as THREE from 'three';

/**
 * ピクセレートパイプライン（本作のルックの核）。
 *
 * シーンを低解像度のレンダーターゲット（例: 480×270）に描き、その結果を
 * NearestFilter で画面いっぱいのクワッドに拡大することでドット絵調にする。
 * EffectComposer を使わず最小構成で実装（依存とビルドを軽く保つ）。
 */
export class PixelPipeline {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly rt: THREE.WebGLRenderTarget;
  private readonly quadScene: THREE.Scene;
  private readonly quadCamera: THREE.OrthographicCamera;
  private readonly quadMaterial: THREE.MeshBasicMaterial;

  /**
   * @param renderer 共有 WebGLRenderer。
   * @param lowWidth  低解像度 RT の幅（既定 480）。
   * @param lowHeight 低解像度 RT の高さ（既定 270）。
   */
  constructor(renderer: THREE.WebGLRenderer, lowWidth = 480, lowHeight = 270) {
    this.renderer = renderer;

    this.rt = new THREE.WebGLRenderTarget(lowWidth, lowHeight, {
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: true,
      stencilBuffer: false,
    });
    this.rt.texture.colorSpace = THREE.SRGBColorSpace;

    // RT を全画面表示するためのクワッド
    this.quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.quadMaterial = new THREE.MeshBasicMaterial({ map: this.rt.texture });
    const quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), this.quadMaterial);
    this.quadScene = new THREE.Scene();
    this.quadScene.add(quad);
  }

  /** 低解像度バッファの解像度を変える（縦横比は呼び出し側で揃える）。 */
  setLowResolution(width: number, height: number): void {
    this.rt.setSize(Math.max(1, Math.floor(width)), Math.max(1, Math.floor(height)));
  }

  /** シーンを低解像度に描いてから画面へニアレスト拡大する。 */
  render(scene: THREE.Scene, camera: THREE.Camera): void {
    // 1) 低解像度 RT へ
    this.renderer.setRenderTarget(this.rt);
    this.renderer.render(scene, camera);

    // 2) RT を画面へニアレスト拡大
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.quadScene, this.quadCamera);
  }

  dispose(): void {
    this.rt.dispose();
    this.quadMaterial.dispose();
  }
}
